import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    Inject,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

interface EatItem {
    id: number;
    title: string;
    description: string;
    shopName: string;
    image: string;
    category: string;
    createdAt: string;
    rating?: number;
    price?: string;
}

interface EatPagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

@Component({
    selector: 'app-community-eat',
    templateUrl: './community-eat.component.html',
    styleUrls: ['./community-eat.component.scss'],
})
export class CommunityEatComponent implements OnInit, OnDestroy {
    public eatList: EatItem[] = [];
    public pagination: EatPagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 6,
    };
    public isLoadingMore: boolean = false;
    public isLoading: boolean = true;
    public hasMoreItems: boolean = true;

    // Static data for now
    private staticData: EatItem[] = [
        {
            id: 1,
            title: 'Hokkien Mee',
            description: 'Kim Keat Hokkien Mee is a stall at Toa Payoh that specialises in their Hokkien mee which is cooked and served in a claypot.',
            shopName: 'Kim Keat Hokkien Mee',
            image: 'https://d2vdwpk8tr4smj.cloudfront.net/images/shops/02/15/42/21542_shop-main-photo_20200930121554.jpg',
            category: 'Local Delights',
            createdAt: '2024-01-15',
            rating: 4.5,
            price: '$8-12'
        },
        {
            id: 2,
            title: 'Char Kway Teow',
            description: 'Traditional wok-fried flat rice noodles with soy sauce, eggs, bean sprouts, and seafood.',
            shopName: 'Hill Street Char Kway Teow',
            image: 'https://d2vdwpk8tr4smj.cloudfront.net/images/shops/02/15/42/21542_shop-main-photo_20200930121554.jpg',
            category: 'Local Delights',
            createdAt: '2024-01-14',
            rating: 4.3,
            price: '$6-10'
        },
        {
            id: 3,
            title: 'Chicken Rice',
            description: 'Singapore\'s national dish - tender poached chicken served with fragrant rice and chili sauce.',
            shopName: 'Tian Tian Hainanese Chicken Rice',
            image: 'https://d2vdwpk8tr4smj.cloudfront.net/images/shops/02/15/42/21542_shop-main-photo_20200930121554.jpg',
            category: 'Local Delights',
            createdAt: '2024-01-13',
            rating: 4.7,
            price: '$5-8'
        },
        {
            id: 4,
            title: 'Laksa',
            description: 'Spicy coconut curry soup with rice noodles, prawns, and fish cakes.',
            shopName: 'Katong Laksa',
            image: 'https://d2vdwpk8tr4smj.cloudfront.net/images/shops/02/15/42/21542_shop-main-photo_20200930121554.jpg',
            category: 'Local Delights',
            createdAt: '2024-01-12',
            rating: 4.4,
            price: '$7-11'
        },
        {
            id: 5,
            title: 'Roti Prata',
            description: 'Crispy flatbread served with curry sauce - perfect for breakfast or any time of day.',
            shopName: 'Springleaf Prata Place',
            image: 'https://d2vdwpk8tr4smj.cloudfront.net/images/shops/02/15/42/21542_shop-main-photo_20200930121554.jpg',
            category: 'Indian',
            createdAt: '2024-01-11',
            rating: 4.2,
            price: '$3-6'
        },
        {
            id: 6,
            title: 'Bak Kut Teh',
            description: 'Herbal pork rib soup - a comforting dish perfect for any meal.',
            shopName: 'Ng Ah Sio Pork Ribs Soup',
            image: 'https://d2vdwpk8tr4smj.cloudfront.net/images/shops/02/15/42/21542_shop-main-photo_20200930121554.jpg',
            category: 'Chinese',
            createdAt: '2024-01-10',
            rating: 4.6,
            price: '$9-15'
        },
        {
            id: 7,
            title: 'Nasi Lemak',
            description: 'Fragrant coconut rice served with fried chicken, ikan bilis, and sambal.',
            shopName: 'Ponggol Nasi Lemak',
            image: 'https://d2vdwpk8tr4smj.cloudfront.net/images/shops/02/15/42/21542_shop-main-photo_20200930121554.jpg',
            category: 'Malay',
            createdAt: '2024-01-09',
            rating: 4.3,
            price: '$6-9'
        },
        {
            id: 8,
            title: 'Satay',
            description: 'Grilled meat skewers served with peanut sauce and ketupat rice cakes.',
            shopName: 'Lau Pa Sat Satay',
            image: 'https://d2vdwpk8tr4smj.cloudfront.net/images/shops/02/15/42/21542_shop-main-photo_20200930121554.jpg',
            category: 'Malay',
            createdAt: '2024-01-08',
            rating: 4.5,
            price: '$8-12'
        },
        {
            id: 9,
            title: 'Fish Head Curry',
            description: 'Rich curry with tender fish head, vegetables, and rice.',
            shopName: 'Muthu\'s Curry',
            image: 'https://d2vdwpk8tr4smj.cloudfront.net/images/shops/02/15/42/21542_shop-main-photo_20200930121554.jpg',
            category: 'Indian',
            createdAt: '2024-01-07',
            rating: 4.4,
            price: '$12-18'
        },
        {
            id: 10,
            title: 'Chilli Crab',
            description: 'Singapore\'s signature dish - fresh crab cooked in spicy tomato sauce.',
            shopName: 'Jumbo Seafood',
            image: 'https://d2vdwpk8tr4smj.cloudfront.net/images/shops/02/15/42/21542_shop-main-photo_20200930121554.jpg',
            category: 'Seafood',
            createdAt: '2024-01-06',
            rating: 4.8,
            price: '$25-35'
        },
        {
            id: 11,
            title: 'Dim Sum',
            description: 'Traditional Chinese bite-sized portions served in steamer baskets.',
            shopName: 'Tim Ho Wan',
            image: 'https://d2vdwpk8tr4smj.cloudfront.net/images/shops/02/15/42/21542_shop-main-photo_20200930121554.jpg',
            category: 'Chinese',
            createdAt: '2024-01-05',
            rating: 4.6,
            price: '$15-25'
        },
        {
            id: 12,
            title: 'Ice Kacang',
            description: 'Shaved ice dessert with red beans, jelly, and syrup - perfect for hot days.',
            shopName: 'Ice Cream Uncle',
            image: 'https://d2vdwpk8tr4smj.cloudfront.net/images/shops/02/15/42/21542_shop-main-photo_20200930121554.jpg',
            category: 'Desserts',
            createdAt: '2024-01-04',
            rating: 4.1,
            price: '$3-5'
        }
    ];

    private destroy$ = new Subject<void>();

    constructor(
        private cdr: ChangeDetectorRef,
        @Inject(PLATFORM_ID) private platformId: object
    ) { }

    ngOnInit(): void {
        this.loadEatItems();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public loadEatItems(page: number = 1, append: boolean = false): void {
        if (!append) {
            this.isLoading = true;
            this.eatList = [];
        } else {
            this.isLoadingMore = true;
        }

        // Simulate API call with static data
        setTimeout(() => {
            const startIndex = (page - 1) * this.pagination.itemsPerPage;
            const endIndex = startIndex + this.pagination.itemsPerPage;
            const pageData = this.staticData.slice(startIndex, endIndex);

            if (append) {
                this.eatList = [...this.eatList, ...pageData];
            } else {
                this.eatList = pageData;
            }

            this.pagination = {
                currentPage: page,
                totalPages: Math.ceil(this.staticData.length / this.pagination.itemsPerPage),
                totalItems: this.staticData.length,
                itemsPerPage: this.pagination.itemsPerPage
            };

            this.hasMoreItems = this.pagination.currentPage < this.pagination.totalPages;

            this.isLoading = false;
            this.isLoadingMore = false;
            this.cdr.markForCheck();
        }, 500); // Simulate network delay
    }

    public loadMore(): void {
        if (!this.isLoadingMore && this.hasMoreItems) {
            this.loadEatItems(this.pagination.currentPage + 1, true);
        }
    }

    public findShops(item: EatItem): void {
        console.log('Finding shops for:', item.title);
        // TODO: Implement navigation to shops page with filter
    }

    public trackById(index: number, item: EatItem): number {
        return item.id;
    }

    public getRatingStars(rating: number): string[] {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 !== 0;

        for (let i = 0; i < fullStars; i++) {
            stars.push('full');
        }
        if (hasHalfStar) {
            stars.push('half');
        }
        while (stars.length < 5) {
            stars.push('empty');
        }
        return stars;
    }
}


